'use client'

import { useT } from '@/lib/i18n'

/**
 * L3 хуки модуля import-wizard.
 *
 * Мастер импорта по своей природе stateful (многошаговый wizard), поэтому
 * основной хук `useImportWizard` ведёт локальный стейт через useState и зовёт
 * ресурс (import-wizard.api). `useBulkUpload` — прямая загрузка файла в DWH.
 * `importWizardKeys` — фабрика queryKey (для будущих React Query потребителей,
 * напр. список шаблонов импорта).
 */

import { useState, useCallback } from 'react'
import { importWizardApi } from './import-wizard.api'
import { ApiError } from '@/lib/api/http'
import type {
  ColumnMatch,
  ValidationSummary,
  DuplicateCandidate,
  ImportPreviewResponse,
} from './import-wizard.types'

// DTO ответов живут рядом с ресурсом (import-wizard.api) и реэкспортятся здесь.
export type {
  UploadedFileDto,
  ImportUploadResponse,
  ImportValidateRequest,
  ImportValidateResponse,
  BulkUploadResponse,
} from './import-wizard.api'

import type { BulkUploadResponse } from './import-wizard.api'

/** Фабрика queryKey модуля import-wizard. */
export const importWizardKeys = {
  templates: ['import-templates'] as const,
}

/**
 * Прямая загрузка файлов в DWH (страница admin/data-loader). Отдаёт стабильную
 * async-функцию: вызывающий сам ведёт пер-файловый прогресс/очередь в своём
 * стейте. Извлекает читаемое сообщение об ошибке из ApiError (ML-service detail).
 */
export function useBulkUpload() {
  const t = useT()
  return useCallback(async (file: File, fileType: string): Promise<BulkUploadResponse> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('fileType', fileType)
    try {
      return await importWizardApi.bulkUpload(formData)
    } catch (e: unknown) {
      throw new Error(extractErrorMessage(e, t('importWizard.error.upload')))
    }
  }, [])
}

export type WizardStep = 'upload' | 'mapping' | 'validate' | 'preview' | 'commit'

/**
 * Извлекает человекочитаемое сообщение об ошибке из ApiError. Роуты
 * /api/import-wizard/* проксируют ML-service (FastAPI), который кладёт детали в
 * `detail`/`message`, а не в `error` — http-ядро их сохраняет в ApiError.body.
 * Без этого FastAPI-ошибки деградировали бы до generic "Ошибка запроса (500)".
 */
function extractErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    const body = e.body as Record<string, unknown> | null | undefined
    if (body && typeof body === 'object') {
      if (typeof body.error === 'string') return body.error
      if (typeof body.detail === 'string') return body.detail
      if (typeof body.detail === 'object' && body.detail) return JSON.stringify(body.detail)
      if (typeof body.message === 'string') return body.message
    }
    return e.message || fallback
  }
  if (e instanceof Error) return e.message
  if (typeof e === 'string') return e
  if (e && typeof e === 'object' && 'detail' in e) return String((e as Record<string, unknown>).detail)
  return fallback
}

/** Parsed file info returned from the upload API */
interface FileUploadInfo {
  headers: string[]
  sampleRows: Record<string, unknown>[]
  totalRows: number
  columnMatches: ColumnMatch[]
}

interface WizardState {
  step: WizardStep
  sessionId: string | null
  // Import file data
  importFile: FileUploadInfo | null
  importMappingConfig: Record<string, string>
  // Export file data
  exportFile: FileUploadInfo | null
  exportMappingConfig: Record<string, string>
  // Legacy single-file fields (kept for backward compat with validate/preview/commit steps)
  headers: string[]
  sampleRows: Record<string, unknown>[]
  totalRows: number
  columnMatches: ColumnMatch[]
  mappingConfig: Record<string, string>
  // Shared state
  validation: ValidationSummary | null
  duplicates: DuplicateCandidate[]
  preview: ImportPreviewResponse | null
  loading: boolean
  error: string | null
}

export function useImportWizard() {
  const t = useT()
  const [state, setState] = useState<WizardState>({
    step: 'upload',
    sessionId: null,
    importFile: null,
    importMappingConfig: {},
    exportFile: null,
    exportMappingConfig: {},
    headers: [],
    sampleRows: [],
    totalRows: 0,
    columnMatches: [],
    mappingConfig: {},
    validation: null,
    duplicates: [],
    preview: null,
    loading: false,
    error: null,
  })

  const uploadFile = useCallback(async (importFile: File, exportFile: File | null) => {
    setState((s) => ({ ...s, loading: true, error: null }))
    try {
      // Upload через /api/import-wizard/upload → проксирует на ML-service (Python, до 300MB)
      const formData = new FormData()
      formData.append('import_file', importFile)
      if (exportFile) formData.append('export_file', exportFile)
      const data = await importWizardApi.upload(formData)

      // Build initial mapping from auto-matched columns for both files
      const importMapping: Record<string, string> = {}
      const exportMapping: Record<string, string> = {}
      if (data.import?.columnMatches) {
        data.import.columnMatches.forEach((m: ColumnMatch) => {
          if (m.status === 'auto') importMapping[m.sourceColumn] = m.targetColumn
        })
      }
      if (data.export?.columnMatches) {
        data.export.columnMatches.forEach((m: ColumnMatch) => {
          if (m.status === 'auto') exportMapping[m.sourceColumn] = m.targetColumn
        })
      }

      // Combined mapping for downstream steps (validate/preview/commit)
      const combinedMapping = { ...importMapping, ...exportMapping }

      setState((s) => ({
        ...s,
        step: 'mapping',
        sessionId: data.sessionId,
        // Import file data
        importFile: data.import
          ? {
              headers: data.import.headers,
              sampleRows: data.import.sampleRows,
              totalRows: data.import.totalRows,
              columnMatches: data.import.columnMatches,
            }
          : null,
        importMappingConfig: importMapping,
        // Export file data
        exportFile: data.export
          ? {
              headers: data.export.headers,
              sampleRows: data.export.sampleRows,
              totalRows: data.export.totalRows,
              columnMatches: data.export.columnMatches,
            }
          : null,
        exportMappingConfig: exportMapping,
        // Legacy fields (use import file as primary for backward compat)
        headers: data.import?.headers ?? data.headers ?? [],
        sampleRows: data.import?.sampleRows ?? data.sampleRows ?? [],
        totalRows: (data.import?.totalRows ?? 0) + (data.export?.totalRows ?? 0),
        columnMatches: data.import?.columnMatches ?? data.columnMatches ?? [],
        mappingConfig: combinedMapping,
        loading: false,
      }))
    } catch (e: unknown) {
      const message = extractErrorMessage(e, t('importWizard.error.unknown'))
      console.error('Import upload error:', e)
      setState((s) => ({ ...s, loading: false, error: message }))
    }
  }, [])

  const setMapping = useCallback((sourceCol: string, targetCol: string, fileType?: 'import' | 'export') => {
    setState((s) => {
      // Determine which config to update
      const configKey = fileType === 'export' ? 'exportMappingConfig' : 'importMappingConfig'
      const newConfig = { ...s[configKey] }

      // Remove any existing mapping to this target in this file's config
      for (const [key, val] of Object.entries(newConfig)) {
        if (val === targetCol) delete newConfig[key]
      }
      if (targetCol) newConfig[sourceCol] = targetCol
      else delete newConfig[sourceCol]

      // Rebuild combined mapping
      const otherConfig = fileType === 'export' ? s.importMappingConfig : s.exportMappingConfig
      const combinedMapping = { ...otherConfig, ...newConfig }

      return {
        ...s,
        [configKey]: newConfig,
        mappingConfig: combinedMapping,
      }
    })
  }, [])

  const validateData = useCallback(async () => {
    if (!state.sessionId) return
    setState((s) => ({ ...s, loading: true, error: null }))
    try {
      const data = await importWizardApi.validate({
        sessionId: state.sessionId,
        mappingConfig: state.mappingConfig,
        importMappingConfig: state.importMappingConfig,
        exportMappingConfig: state.exportMappingConfig,
      })
      setState((s) => ({
        ...s,
        step: 'validate',
        validation: data.validation,
        duplicates: data.duplicates,
        loading: false,
      }))
    } catch (e: unknown) {
      const message = extractErrorMessage(e, t('importWizard.error.unknown'))
      setState((s) => ({ ...s, loading: false, error: message }))
    }
  }, [state.sessionId, state.mappingConfig, state.importMappingConfig, state.exportMappingConfig])

  const previewData = useCallback(async () => {
    if (!state.sessionId) return
    setState((s) => ({ ...s, loading: true, error: null }))
    try {
      const data = await importWizardApi.preview({ sessionId: state.sessionId })
      setState((s) => ({ ...s, step: 'preview', preview: data, loading: false }))
    } catch (e: unknown) {
      const message = extractErrorMessage(e, t('importWizard.error.unknown'))
      setState((s) => ({ ...s, loading: false, error: message }))
    }
  }, [state.sessionId])

  const commitData = useCallback(
    async (duplicateActions?: Record<number, string>) => {
      if (!state.sessionId) return
      setState((s) => ({ ...s, loading: true, error: null }))
      try {
        const data = await importWizardApi.commit({ sessionId: state.sessionId, duplicateActions })
        setState((s) => ({ ...s, step: 'commit', loading: false }))
        return data
      } catch (e: unknown) {
        const message = extractErrorMessage(e, t('importWizard.error.unknown'))
        setState((s) => ({ ...s, loading: false, error: message }))
      }
    },
    [state.sessionId],
  )

  const goToStep = useCallback((step: WizardStep) => {
    setState((s) => ({ ...s, step, error: null }))
  }, [])

  const reset = useCallback(() => {
    setState({
      step: 'upload',
      sessionId: null,
      importFile: null,
      importMappingConfig: {},
      exportFile: null,
      exportMappingConfig: {},
      headers: [],
      sampleRows: [],
      totalRows: 0,
      columnMatches: [],
      mappingConfig: {},
      validation: null,
      duplicates: [],
      preview: null,
      loading: false,
      error: null,
    })
  }, [])

  return { ...state, uploadFile, setMapping, validateData, previewData, commitData, goToStep, reset }
}
